import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { IconButton, Menu, MenuItem, Popover } from '@material-ui/core';
import { MdMoreVert, MdRemoveRedEye, MdEdit, MdDelete, MdPublish, MdCancel, MdAttachFile } from 'react-icons/md';
import { actions as toastrActions } from 'react-redux-toastr';
import Paper from 'components/Paper';
import { LoadingPage } from 'components/Loading';
import Images from 'assets/img';
import Button from 'components/Button';
import { Row } from 'styles/components';
import { embarcadoresAPI, fretesAPI } from 'services/apiRest';
import ModalCancelamento from './components/ModalCancelamento';
import ModalAprovacao from './components/ModalAprovacao';
import ModalAnexarXml from './components/ModalAnexarXml';
import {
  Container,
  Table,
  Th,
  Td,
  Tr,
  HeaderRow,
  WrapperPopover,
  PopoverText,
  PopoverButton,
} from './styles';

const formatCpfCnpj = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  if (digits.length === 11) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  if (digits.length === 14) {
    return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  return value || '';
};

const getEmbarcadorLabel = (e) => {
  if (!e) return '';
  const nome = e.nome || e.nome_fantasia || e.nomeFantasia || 'Sem nome';
  const doc = e.cpfCnpj || e.cpf_cnpj || e.cnpj || e.cpf;
  const docFmt = formatCpfCnpj(doc);
  return docFmt ? `${nome} - ${docFmt}` : nome;
};

const getStatusLabel = (status) => {
  const statusMap = {
    aguardando_cotacao: 'Aguardando Cotação',
    aguardando_aprovacao: 'Aguardando Aprovação',
    buscando_motorista: 'Buscando Motorista',
    motorista_encontrado: 'Motorista Encontrado',
    aguardando_carregamento: 'Aguardando Carregamento',
    emitindo_documentos: 'Emitindo Documentos',
    viagem_iniciada: 'Viagem Iniciada',
    viagem_encerrada: 'Viagem Encerrada',
    cancelado: 'Cancelado',
  };
  return statusMap[status] || (status ? status : 'Não Publicado');
};

const getStatusColor = (status) => {
  const colorMap = {
    aguardando_cotacao: '#ff9800',        // Laranja
    aguardando_aprovacao: '#ffc107',      // Amarelo
    buscando_motorista: '#2196f3',        // Azul
    motorista_encontrado: '#03a9f4',      // Azul claro
    aguardando_carregamento: '#9c27b0',   // Roxo
    emitindo_documentos: '#673ab7',       // Roxo escuro
    viagem_iniciada: '#4caf50',           // Verde
    viagem_encerrada: '#607d8b',          // Cinza
    cancelado: '#f44336',                 // Vermelho
  };
  return colorMap[status] || (status ? '#666' : '#999');
};

export default function Fretes() {
  const history = useHistory();
  const dispatch = useDispatch();
  const [fretes, setFretes] = useState([]);
  const [embarcadores, setEmbarcadores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuFreteId, setMenuFreteId] = useState(null);
  const [modalCancelamentoOpen, setModalCancelamentoOpen] = useState(false);
  const [freteParaCancelar, setFreteParaCancelar] = useState(null);
  const [modalAprovacaoOpen, setModalAprovacaoOpen] = useState(false);
  const [freteParaAprovar, setFreteParaAprovar] = useState(null);
  const [modalAnexarXmlOpen, setModalAnexarXmlOpen] = useState(false);
  const [freteParaAnexarXml, setFreteParaAnexarXml] = useState(null);

  const embarcadorPorId = React.useMemo(() => {
    const map = new Map();
    (embarcadores || []).forEach((e) => map.set(e.id, e));
    return map;
  }, [embarcadores]);

  const carregar = async () => {
    try {
      setLoading(true);
      const [list, embarcadoresList] = await Promise.all([
        fretesAPI.listar(),
        embarcadoresAPI.listar(),
      ]);
      setFretes(Array.isArray(list) ? list : []);
      setEmbarcadores(Array.isArray(embarcadoresList) ? embarcadoresList : []);
    } catch (e) {
      console.error('[Fretes] Erro ao listar fretes:', e);
      setFretes([]);
      setEmbarcadores([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedId) return;
    try {
      await fretesAPI.deletar(selectedId);
      dispatch(
        toastrActions.add({
          type: 'success',
          title: 'Sucesso',
          message: 'Frete excluído com sucesso!',
        })
      );
      await carregar();
    } catch (e) {
      console.error('[Fretes] Erro ao deletar frete:', e);
      dispatch(
        toastrActions.add({
          type: 'error',
          title: 'Erro',
          message: 'Erro ao excluir o frete.',
        })
      );
    } finally {
      setOpen(false);
      setAnchorEl(null);
      setSelectedId(null);
    }
  };

  const handleMenuOpen = (event, freteId) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuFreteId(freteId);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuFreteId(null);
  };


  const handleAbrirModalCancelamento = () => {
    const frete = fretes.find((f) => f.id === menuFreteId);
    if (!frete) return;
    
    if (frete.status === 'encerrado') {
      dispatch(
        toastrActions.add({
          type: 'warning',
          title: 'Atenção',
          message: 'Não é possível cancelar um frete que já foi encerrado.',
        })
      );
      handleMenuClose();
      return;
    }

    setFreteParaCancelar(frete);
    setModalCancelamentoOpen(true);
    handleMenuClose();
  };

  const handleConfirmarCancelamento = async (motivo) => {
    if (!freteParaCancelar) return;
    try {
      await fretesAPI.cancelar(freteParaCancelar.id, motivo);
      dispatch(
        toastrActions.add({
          type: 'success',
          title: 'Sucesso',
          message: 'Frete cancelado com sucesso!',
        })
      );
      setModalCancelamentoOpen(false);
      setFreteParaCancelar(null);
      await carregar();
    } catch (e) {
      console.error('[Fretes] Erro ao cancelar frete:', e);
      dispatch(
        toastrActions.add({
          type: 'error',
          title: 'Erro',
          message: e.response?.data?.detalhes || e.message || 'Erro ao cancelar o frete.',
        })
      );
    }
  };

  const handleAbrirModalAprovacao = () => {
    const frete = fretes.find((f) => f.id === menuFreteId);
    if (!frete) return;
    
    // Validação: não pode aprovar sem valor do frete
    if (!frete.valor_frete || frete.valor_frete <= 0) {
      dispatch(
        toastrActions.add({
          type: 'error',
          title: 'Erro',
          message: 'Não é possível aprovar a cotação sem o valor do frete preenchido. Por favor,aguarde até que o valor do frete seja informado.',
        })
      );
      handleMenuClose();
      return;
    }
    
    setFreteParaAprovar(frete);
    setModalAprovacaoOpen(true);
    handleMenuClose();
  };

  const handleConfirmarAprovacao = async (acao, justificativa) => {
    if (!freteParaAprovar) return;
    try {
      if (acao === 'aprovar') {
        // Validação extra antes de aprovar
        if (!freteParaAprovar.valor_frete || freteParaAprovar.valor_frete <= 0) {
          throw new Error('Não é possível aprovar a cotação sem o valor do frete preenchido');
        }
        
        await fretesAPI.aprovar(freteParaAprovar.id);
        dispatch(
          toastrActions.add({
            type: 'success',
            title: 'Sucesso',
            message: 'Cotação aprovada com sucesso!',
          })
        );
      } else if (acao === 'reprovar') {
        await fretesAPI.reprovar(freteParaAprovar.id, justificativa);
        dispatch(
          toastrActions.add({
            type: 'success',
            title: 'Sucesso',
            message: 'Cotação reprovada com sucesso!',
          })
        );
      }
      setModalAprovacaoOpen(false);
      setFreteParaAprovar(null);
      await carregar();
    } catch (e) {
      console.error('[Fretes] Erro ao processar aprovação:', e);
      dispatch(
        toastrActions.add({
          type: 'error',
          title: 'Erro',
          message: e.response?.data?.detalhes || e.message || 'Erro ao processar a ação.',
        })
      );
      throw e;
    }
  };

  const handlePublicarFrete = async () => {
    if (!menuFreteId) return;
    try {
      await fretesAPI.publicar(menuFreteId);
      dispatch(
        toastrActions.add({
          type: 'success',
          title: 'Sucesso',
          message: 'Frete publicado com sucesso!',
        })
      );
      handleMenuClose();
      await carregar();
    } catch (e) {
      console.error('[Fretes] Erro ao publicar frete:', e);
      dispatch(
        toastrActions.add({
          type: 'error',
          title: 'Erro',
          message: e.response?.data?.detalhes || e.message || 'Erro ao publicar o frete.',
        })
      );
      handleMenuClose();
    }
  };

  const handleAnexarXmls = async (xmlsParsed) => {
    if (!freteParaAnexarXml) return;
    try {
      let sucesso = 0;
      let erros = 0;
      const errosDetalhes = [];
      
      // Anexar cada XML individualmente
      for (const xmlData of xmlsParsed) {
        try {
          await fretesAPI.anexarXml(freteParaAnexarXml.id, xmlData);
          sucesso++;
        } catch (err) {
          erros++;
          const mensagem = err.response?.data?.detalhes || err.message || 'Erro desconhecido';
          errosDetalhes.push(`${xmlData.xml_filename}: ${mensagem}`);
          console.error(`[Fretes] Erro ao anexar ${xmlData.xml_filename}:`, err);
        }
      }
      
      // Feedback ao usuário
      if (sucesso > 0 && erros === 0) {
        dispatch(
          toastrActions.add({
            type: 'success',
            title: 'Sucesso',
            message: `${sucesso} XML(s) anexado(s) com sucesso!`,
          })
        );
      } else if (sucesso > 0 && erros > 0) {
        const mensagemDetalhada = `✅ ${sucesso} XML(s) anexado(s) com sucesso\n❌ ${erros} falharam:\n${errosDetalhes.map(e => `• ${e}`).join('\n')}`;
        dispatch(
          toastrActions.add({
            type: 'warning',
            title: 'Atenção',
            message: mensagemDetalhada,
          })
        );
        console.warn('[Fretes] Resultado da anexação:', { sucesso, erros, detalhes: errosDetalhes });
      } else {
        const mensagemErro = `Nenhum XML foi anexado:\n${errosDetalhes.map(e => `• ${e}`).join('\n')}`;
        dispatch(
          toastrActions.add({
            type: 'error',
            title: 'Erro',
            message: mensagemErro,
          })
        );
        throw new Error('Nenhum XML foi anexado');
      }
      
      setModalAnexarXmlOpen(false);
      setFreteParaAnexarXml(null);
      await carregar();
    } catch (e) {
      console.error('[Fretes] Erro ao anexar XMLs:', e);
      if (!e.message.includes('Nenhum XML foi anexado')) {
        dispatch(
          toastrActions.add({
            type: 'error',
            title: 'Erro',
            message: e.response?.data?.detalhes || e.message || 'Erro ao anexar XMLs.',
          })
        );
      }
      throw e;
    }
  };

  const handleAction = (action, freteId) => {
    const frete = fretes.find((f) => f.id === freteId);
    if (!frete) return;

    switch (action) {
      case 'view':
        history.push(`/embarcadores/fretes/view/${freteId}`);
        break;
      case 'edit':
        history.push(`/embarcadores/fretes/edit/${freteId}`);
        break;
      case 'delete':
        setSelectedId(freteId);
        setAnchorEl(menuAnchorEl);
        setOpen(true);
        break;
      case 'cancel':
        handleAbrirModalCancelamento();
        return;
      case 'aprovar':
        handleAbrirModalAprovacao();
        return;
      case 'publicar':
        handlePublicarFrete();
        return;
      case 'anexarXml':
        setFreteParaAnexarXml(frete);
        setModalAnexarXmlOpen(true);
        handleMenuClose();
        return;
      default:
        break;
    }
    handleMenuClose();
  };

  useEffect(() => {
    carregar();
  }, []);

  return (
    <div style={{ padding: '20px', width: '100%' }}>
      <Paper
        title="Fretes"
        icon={Images.IconPaper}
        rightContent={
          <div style={{ display: 'flex', gap: '10px' }}>
            <Button label="Atualizar" type="button" onClick={carregar} />
            <Button label="Novo Frete" type="button" onClick={() => history.push('/embarcadores/fretes/new')} />
          </div>
        }
      >
        <Container>
          {loading ? (
            <LoadingPage />
          ) : (
            <Table>
              <thead>
                <HeaderRow>
                  <Th>Ações</Th>
                  <Th>Referência</Th>
                  <Th>Status</Th>
                  <Th>Embarcador</Th>
                  <Th>Coleta (CEP/Cidade-UF)</Th>
                  <Th>Entrega (CEP/Cidade-UF)</Th>
                  <Th>Valor</Th>
                </HeaderRow>
              </thead>
              <tbody>
                {fretes.map((f) => (
                  <Tr key={f.id}>
                    <Td>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, f.id)}
                        style={{ padding: '4px' }}
                      >
                        <MdMoreVert size={20} style={{ color: '#122d58' }} />
                      </IconButton>
                    </Td>
                    <Td>{f.numero ? `FRETE Nº ${f.numero}` : '-'}</Td>
                    <Td>
                      <span
                        style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          backgroundColor: getStatusColor(f.status) + '20',
                          color: getStatusColor(f.status),
                          fontSize: '12px',
                          fontWeight: 600,
                          display: 'block',
                          width: '100%',
                          textAlign: 'center',
                          marginRight: '10px',
                          marginLeft: '10px',
                          marginTop: '10px',
                          marginBottom: '10px',
                          paddingRight: '5px',
                        }}
                      >
                        {getStatusLabel(f.status)}
                      </span>
                    </Td>
                    <Td title={f.embarcador_id}>
                      {getEmbarcadorLabel(embarcadorPorId.get(f.embarcador_id)) || '-'}
                    </Td>
                    <Td>{`${f.collect_zipcode || ''} ${f.collect_city || ''}-${f.collect_state || ''}`.trim()}</Td>
                    <Td>{`${f.delivery_zipcode || ''} ${f.delivery_city || ''}-${f.delivery_state || ''}`.trim()}</Td>
                    <Td>
                      {f.valor_frete 
                        ? new Intl.NumberFormat('pt-BR', { 
                            style: 'currency', 
                            currency: 'BRL' 
                          }).format(f.valor_frete)
                        : '-'
                      }
                    </Td>
                  </Tr>
                ))}
                {fretes.length === 0 ? (
                  <Tr>
                    <Td colSpan={7}>Nenhum frete cadastrado.</Td>
                  </Tr>
                ) : null}
              </tbody>
            </Table>
          )}
        </Container>
      </Paper>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem
          onClick={() => handleAction('view', menuFreteId)}
          style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
        >
          <MdRemoveRedEye size={18} style={{ color: '#122d58' }} />
          Visualizar
        </MenuItem>
        {fretes.find((f) => f.id === menuFreteId)?.status !== 'cancelado' && (
          <MenuItem
            onClick={() => handleAction('edit', menuFreteId)}
            style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
          >
            <MdEdit size={18} style={{ color: '#4caf50' }} />
            Editar
          </MenuItem>
        )}
        {fretes.find((f) => f.id === menuFreteId)?.status !== 'cancelado' && (
          <MenuItem
            onClick={() => handleAction('anexarXml', menuFreteId)}
            style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
          >
            <MdAttachFile size={18} style={{ color: '#2196f3' }} />
            Anexar XMLs
          </MenuItem>
        )}
        {(!fretes.find((f) => f.id === menuFreteId)?.status || 
          fretes.find((f) => f.id === menuFreteId)?.status === null) && (
          <MenuItem
            onClick={() => handleAction('publicar', menuFreteId)}
            style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
          >
            <MdPublish size={18} style={{ color: '#4caf50' }} />
            Publicar Frete
          </MenuItem>
        )}
        {(fretes.find((f) => f.id === menuFreteId)?.status === 'aguardando_cotacao' ||
          fretes.find((f) => f.id === menuFreteId)?.status === 'aguardando_aprovacao') && (
          <MenuItem
            onClick={() => handleAction('aprovar', menuFreteId)}
            style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
          >
            <MdPublish size={18} style={{ color: '#2196f3' }} />
            Aprovar Cotação
          </MenuItem>
        )}
        {fretes.find((f) => f.id === menuFreteId)?.status !== 'viagem_encerrada' &&
        fretes.find((f) => f.id === menuFreteId)?.status !== 'cancelado' ? (
          <MenuItem
            onClick={() => handleAction('cancel', menuFreteId)}
            style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
          >
            <MdCancel size={18} style={{ color: '#f44336' }} />
            Cancelar
          </MenuItem>
        ) : null}
        {fretes.find((f) => f.id === menuFreteId)?.status !== 'cancelado' && (
          <MenuItem
            onClick={() => handleAction('delete', menuFreteId)}
            style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
          >
            <MdDelete size={18} style={{ color: '#f44336' }} />
            Excluir
          </MenuItem>
        )}
      </Menu>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => {
          setOpen(false);
          setAnchorEl(null);
          setSelectedId(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <WrapperPopover>
          <PopoverText>Tem certeza que deseja excluir?</PopoverText>
          <Row>
            <PopoverButton onClick={handleDelete} primary>
              Sim
            </PopoverButton>
            <PopoverButton
              onClick={() => {
                setOpen(false);
                setAnchorEl(null);
                setSelectedId(null);
              }}
            >
              Não
            </PopoverButton>
          </Row>
        </WrapperPopover>
      </Popover>

      <ModalCancelamento
        open={modalCancelamentoOpen}
        onClose={() => {
          setModalCancelamentoOpen(false);
          setFreteParaCancelar(null);
        }}
        onConfirm={handleConfirmarCancelamento}
        frete={freteParaCancelar}
      />

      <ModalAprovacao
        open={modalAprovacaoOpen}
        onClose={() => {
          setModalAprovacaoOpen(false);
          setFreteParaAprovar(null);
        }}
        onConfirm={handleConfirmarAprovacao}
      />

      <ModalAnexarXml
        open={modalAnexarXmlOpen}
        onClose={() => {
          setModalAnexarXmlOpen(false);
          setFreteParaAnexarXml(null);
        }}
        onConfirm={handleAnexarXmls}
        freteId={freteParaAnexarXml?.id}
      />
    </div>
  );
}


